/**
 * useActiveNotificationView
 *
 * Publishes focused foreground view state for notification suppression.
 *
 * Primary responsibilities:
 * - Heartbeat only while the current tab is visible and focused
 * - Clear active state on blur, hide, unmount, and pagehide
 *
 * Dependencies:
 * - Active notification view API route
 */

'use client'

import { useEffect, useRef } from 'react'

import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import {
  ACTIVE_NOTIFICATION_VIEW_HEARTBEAT_MS,
  ACTIVE_NOTIFICATION_VIEW_TAB_STORAGE_KEY,
  isNotificationActiveViewScopeId,
  type NotificationActiveViewScopeType
} from '@/lib/notifications/active-view'

type ActiveNotificationViewOptions = {
  enabled?: boolean
  scopeId: string | null | undefined
  scopeType: NotificationActiveViewScopeType
}

function createTabId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }

  return `${Date.now().toString(36)}:${Math.random().toString(36).slice(2)}`
}

function getTabId() {
  if (typeof window === 'undefined') {
    return createTabId()
  }

  const stored = window.sessionStorage.getItem(
    ACTIVE_NOTIFICATION_VIEW_TAB_STORAGE_KEY
  )

  if (stored) {
    return stored
  }

  const tabId = createTabId()
  window.sessionStorage.setItem(
    ACTIVE_NOTIFICATION_VIEW_TAB_STORAGE_KEY,
    tabId
  )
  return tabId
}

function isForegroundTab() {
  if (typeof document === 'undefined') {
    return false
  }

  return document.visibilityState === 'visible' && document.hasFocus()
}

// --------------

export function useActiveNotificationView({
  enabled = true,
  scopeId,
  scopeType
}: ActiveNotificationViewOptions) {
  const lastPublishedActiveRef = useRef<boolean | null>(null)
  const tabIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!enabled || !isNotificationActiveViewScopeId(scopeId)) {
      return
    }

    tabIdRef.current = tabIdRef.current ?? getTabId()
    const tabId = tabIdRef.current
    let disposed = false

    const publish = (active: boolean, force = false) => {
      if (disposed || (!force && lastPublishedActiveRef.current === active)) {
        return
      }

      lastPublishedActiveRef.current = active
      void authenticatedFetch('/api/notifications/active-view', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          active,
          scopeId,
          scopeType,
          tabId
        }),
        keepalive: !active
      }).catch(() => {
        if (lastPublishedActiveRef.current === active) {
          lastPublishedActiveRef.current = null
        }
      })
    }

    const publishCurrentState = (force = false) => {
      publish(isForegroundTab(), force)
    }

    publishCurrentState(true)

    const heartbeat = window.setInterval(() => {
      if (isForegroundTab()) {
        publish(true, true)
        return
      }

      publish(false)
    }, ACTIVE_NOTIFICATION_VIEW_HEARTBEAT_MS)

    const handleFocus = () => publishCurrentState()
    const handleBlur = () => publish(false)
    const handleVisibilityChange = () => publishCurrentState()
    const handlePageHide = () => publish(false, true)

    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)
    window.addEventListener('pagehide', handlePageHide)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      publish(false, true)
      disposed = true
      window.clearInterval(heartbeat)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('pagehide', handlePageHide)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled, scopeId, scopeType])
}
